import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { JOURNAL_ENTRIES } from "@/lib/content";

/**
 * FeaturedPullquote — an editorial strip surfacing the most recent Journal
 * entry as a large italic pullquote. Distinct from the 3-card journal grid;
 * this treats a single essay like a magazine spread's opening spread.
 *
 * Pulls the most recent JOURNAL_ENTRIES[0] and its embedded pullquote block.
 * If the entry has no pullquote block, falls back to the excerpt.
 */
export default function FeaturedPullquote() {
  const latest = JOURNAL_ENTRIES[0];
  if (!latest) return null;

  // Find the first pullquote block; fall back to the excerpt.
  const pullquoteBlock = Array.isArray(latest.body)
    ? latest.body.find((b) => b?.type === "pullquote")
    : null;
  const quoteText = pullquoteBlock?.text || latest.excerpt || "";

  return (
    <section
      className="bg-linen grain py-24 md:py-32 relative overflow-hidden"
      data-testid="featured-pullquote-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left rail — issue meta */}
          <aside className="lg:col-span-4">
            <div className="flex items-baseline gap-3">
              <div className="text-[10px] uppercase tracking-[0.36em] text-brass font-medium">
                Featured in this Issue
              </div>
              <Star size={9} className="text-brass fill-brass" />
            </div>

            <div className="mt-8 hairline-top pt-6">
              <div className="font-cormorant italic text-brass text-lg leading-tight">
                {latest.no}
              </div>
              <div className="mt-1 text-[10px] uppercase tracking-[0.28em] text-slate2/70">
                {latest.date} · {latest.category}
              </div>
            </div>

            <h3
              className="mt-10 font-display text-2xl md:text-3xl text-navy leading-[1.1]"
              data-testid="featured-pullquote-title"
            >
              {latest.title}
            </h3>

            <div className="mt-6 flex items-center gap-3">
              <div className="text-[10px] uppercase tracking-[0.28em] text-slate2/70">
                By {latest.author}
              </div>
              <span className="w-6 h-px bg-brass/50" />
              <div className="text-[10px] uppercase tracking-[0.28em] text-slate2/70">
                {latest.minutes} min read
              </div>
            </div>

            <div className="mt-10">
              <Link
                to={`/journal/${latest.slug}`}
                className="text-[11px] uppercase tracking-[0.32em] text-oxblood hover:text-navy transition-colors font-medium border-b border-brass/60 hover:border-navy pb-1"
                data-testid="featured-pullquote-cta"
              >
                Continue Reading →
              </Link>
            </div>
          </aside>

          {/* Right column — the pullquote itself, magazine-large */}
          <div className="lg:col-span-8 relative">
            {/* oversized italic open-quote glyph, brass, low-opacity */}
            <div
              aria-hidden="true"
              className="absolute -top-8 -left-2 md:-left-6 font-cormorant italic text-brass/25 select-none pointer-events-none"
              style={{ fontSize: "clamp(120px, 18vw, 240px)", lineHeight: 1 }}
            >
              &ldquo;
            </div>

            <blockquote
              className="relative font-cormorant italic text-navy leading-[1.15] pl-6 md:pl-12 pt-6 md:pt-8"
              style={{ fontSize: "clamp(1.75rem, 3.4vw, 2.75rem)" }}
              data-testid="featured-pullquote-text"
            >
              {quoteText}
            </blockquote>

            <div className="mt-10 md:mt-14 pl-6 md:pl-12">
              <div className="flex items-center gap-4">
                <span className="w-16 h-px bg-brass" />
                <span className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium">
                  From The Journal
                </span>
                <Star size={7} className="text-brass fill-brass" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
